import { CalendarDays, MapPin, ArrowUpRight, Headset } from "lucide-react";
import { useApi } from "@/hooks/useApi";
import { useSeo } from "@/hooks/useSeo";
import { useLang } from "@/i18n/LanguageContext";
import PageHero from "@/components/site/PageHero";
import { Reveal, SectionHead } from "@/components/site/Reveal";

const EventCard = ({ ev, past, badge, t }) => {
  const tx = t(`eventsText.${ev.slug}`) || {};
  const title = tx.title || ev.title;
  const date = tx.date || ev.date;
  const location = tx.location || ev.location;
  const category = tx.category || ev.category;
  const description = tx.description || ev.description;
  return (
    <Reveal>
      <article className="grid grid-cols-1 lg:grid-cols-12 gap-0 border border-[#1C1B18]/10 bg-[#F7F5F0] overflow-hidden" data-testid={`event-card-${ev.slug}`}>
        <div className={`lg:col-span-5 relative overflow-hidden ${ev.imageContain ? "bg-[#121212] flex items-center justify-center" : ""}`} style={{ minHeight: 280 }}>
          <img
            src={ev.image}
            alt={`${title} — ${location}`}
            loading="lazy"
            className={ev.imageContain ? "max-h-[420px] w-auto max-w-full object-contain" : "absolute inset-0 w-full h-full object-cover"}
          />
          {!ev.imageContain && <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#F7F5F0]/10" />}
          {past && (
            <span className="absolute top-4 left-4 bg-[#121212]/85 text-[#F7F5F0] border border-[#C5A059]/50 px-4 py-2 text-[10px] tracking-[0.25em] uppercase" data-testid={`event-past-badge-${ev.slug}`}>
              {badge}
            </span>
          )}
        </div>
        <div className="lg:col-span-7 p-8 lg:p-12">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A059] font-medium">{category}</p>
          <h3 className="font-serif-display text-2xl lg:text-4xl mt-4 leading-tight">{title}</h3>
          <div className="flex flex-wrap gap-6 mt-6 text-sm text-[#666157]">
            <span className="inline-flex items-center gap-2"><CalendarDays size={15} className="text-[#9E7B36]" /> {date}</span>
            <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-[#9E7B36]" /> {location}</span>
          </div>
          <p className="mt-6 text-base leading-relaxed text-[#666157]">{description}</p>
          <div className="mt-6 flex items-center gap-3 text-sm text-[#1C1B18]">
            <Headset size={16} className="text-[#C5A059]" />
            <span className="font-serif-display italic text-lg">{t("eventi.standLine")}</span>
          </div>
          {ev.links?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-4">
              {ev.links.map((l, i) => (
                <a
                  key={l.url}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`event-link-${ev.slug}`}
                  className="inline-flex items-center gap-2.5 border border-[#1C1B18]/25 px-6 py-3 text-[10px] tracking-[0.25em] uppercase hover:border-[#C5A059] hover:text-[#9E7B36] transition-colors duration-500"
                >
                  {tx.linkLabel && i === 0 ? tx.linkLabel : l.label} <ArrowUpRight size={12} />
                </a>
              ))}
            </div>
          )}
        </div>
      </article>
    </Reveal>
  );
};

const EventiPage = () => {
  const { t } = useLang();
  const { data: events } = useApi("/events");
  useSeo("eventi");

  const upcoming = (events || []).filter((e) => e.status !== "past");
  const past = (events || []).filter((e) => e.status === "past");

  return (
    <div data-testid="eventi-page">
      <PageHero
        testId="eventi-hero"
        image="/assets/moncalvo-1.webp"
        eyebrow={t("eventi.eyebrow")}
        lines={[t("eventi.titleA"), t("eventi.titleB")]}
        subtitle={t("eventi.heroSub")}
      />

      <section className="py-24 lg:py-32" data-testid="events-list-section">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10">
          <SectionHead eyebrow={t("eventi.listEyebrow")} title={t("eventi.listTitle")} />

          <div className="mt-14 space-y-10" data-testid="events-upcoming-list">
            {upcoming.map((ev) => (
              <EventCard key={ev.slug} ev={ev} past={false} badge={t("eventi.pastBadge")} t={t} />
            ))}
          </div>

          {past.length > 0 && (
            <div className="mt-24 lg:mt-32 pt-16 border-t border-[#1C1B18]/10" data-testid="events-past-section">
              <SectionHead eyebrow={t("eventi.pastEyebrow")} title={t("eventi.pastTitle")} />
              <div className="mt-14 space-y-10" data-testid="events-past-list">
                {past.map((ev) => (
                  <EventCard key={ev.slug} ev={ev} past badge={t("eventi.pastBadge")} t={t} />
                ))}
              </div>
            </div>
          )}

          <Reveal delay={0.2}>
            <p className="mt-14 font-serif-display italic text-xl text-[#666157]" data-testid="events-note">
              {t("eventi.note")}
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default EventiPage;
